import { useLocalization } from '@lib/localization';
import {
  reportDesignLimits, type CreateReportDesignInput, type ReportDesignOwner, type ReportDesignSummary,
} from '@store/api/reportDesignEndpoints';
import { useEffect, useRef, useState } from 'react';
import DesignDialog from './DesignDialog';
import { NameField, OwnerField } from './DesignFields';

interface Props {
  /** The design being copied, or null while the dialog is closed. */
  design: ReportDesignSummary | null;
  /** Where the reader may create a design. Never empty while the dialog is open. */
  owners: ReportDesignOwner[];
  saving: boolean;
  onClose: () => void;
  onCreate: (input: CreateReportDesignInput) => void;
}

interface Draft {
  name: string;
  owner: ReportDesignOwner | null;
}

/**
 * Copies a design under a name and an owner of its own. Also the only way a design changes
 * owner: the copy may belong to another team, or to the organization.
 */
const CopyDesignDialog: React.FC<Props> = ({ design, owners, saving, onClose, onCreate }) => {
  const { t } = useLocalization();

  const [draft, setDraft] = useState<Draft>({ name: '', owner: null });

  // Filled once for each design the dialog opens on, and never again while it is open: what
  // the reader typed is not replaced because something the dialog reads changed under it.
  const filledFor = useRef<string | null>(null);

  useEffect(() => {
    if (!design) {
      filledFor.current = null;

      return;
    }

    if (filledFor.current === design._id) return;

    filledFor.current = design._id;

    // Beside the original where the reader may create there: the usual meaning of a duplicate.
    // Elsewhere only where there is one place to choose from and it is a team. The copy of a
    // team's design is never the organization's unasked: every member of every team would
    // read it.
    const beside = owners.find((owner) => owner.teamId === design.teamId);
    const onlyTeam = owners.length === 1 && owners[0].teamId !== null ? owners[0] : null;

    setDraft({
      // Marked as the copy it is, as far as a name's length lets it.
      name: `${design.name} ${t('Customization_CopySuffix')}`.slice(0, reportDesignLimits.nameLength),
      owner: beside ?? onlyTeam,
    });
  }, [design, owners, t]);

  const submit = () => {
    if (!design || !draft.owner) return;

    onCreate({
      teamId: draft.owner.teamId,
      name: draft.name.trim(),
      // Sent rather than left to the API's default: the dialog does not ask for one, so the
      // copy keeps its source's.
      description: design.description,
      from: { designId: design._id },
    });
  };

  return (
    <DesignDialog
      open={design !== null}
      title={t('Customization_Duplicate')}
      submitLabel={t('Customization_Duplicate')}
      canSubmit={draft.name.trim() !== '' && draft.owner !== null}
      saving={saving}
      onClose={onClose}
      onSubmit={submit}
    >
      <NameField
        value={draft.name}
        onChange={(name) => setDraft((current) => ({ ...current, name }))}
        disabled={saving}
      />

      <OwnerField
        owners={owners}
        value={draft.owner}
        onChange={(owner) => setDraft((current) => ({ ...current, owner }))}
        disabled={saving}
      />
    </DesignDialog>
  );
};

export default CopyDesignDialog;
